import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Footer from '../components/layout/Footer';
import SEO, { PAGE_SEO } from '../components/SEO';
import { ArrowRight, Sparkles, BookOpen, Play, Brain, ShoppingBag, ChevronRight, Trophy, Search } from 'lucide-react';
import { useEffect, useState, useCallback } from 'react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Hero images for carousel
const heroImages = [
  {
    url: "https://images.pexels.com/photos/6925487/pexels-photo-6925487.jpeg?auto=compress&cs=tinysrgb&w=1200",
    alt: "Skincare routine"
  },
  {
    url: "https://images.pexels.com/photos/30797176/pexels-photo-30797176.jpeg?auto=compress&cs=tinysrgb&w=1200",
    alt: "Beauty model"
  },
  {
    url: "https://images.pexels.com/photos/16233812/pexels-photo-16233812.jpeg?auto=compress&cs=tinysrgb&w=1200",
    alt: "Luxury serums"
  },
  {
    url: "https://images.pexels.com/photos/32291052/pexels-photo-32291052.jpeg?auto=compress&cs=tinysrgb&w=1200",
    alt: "Skincare products"
  }
];

// Typewriter words
const typewriterWords = ["Skin", "Hair", "Beauty", "Glow", "Health"];

export default function Landing() {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [displayText, setDisplayText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [leaderboard, setLeaderboard] = useState([]);
  const [stats, setStats] = useState({ content: "10+", quizzes: "8", products: "100K+" });

  // Fetch homepage stats
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/stats/homepage`)
      .then(res => res.json())
      .then(data => {
        if (data.formatted) {
          setStats(data.formatted);
        }
      })
      .catch(console.error);
  }, []);

  // Hero image carousel
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard');
      return;
    }

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) => (prev + 1) % heroImages.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [isAuthenticated, navigate]);

  // Typewriter effect
  useEffect(() => {
    const currentWord = typewriterWords[currentWordIndex];
    const timeout = setTimeout(() => {
      if (!isDeleting) {
        if (displayText.length < currentWord.length) {
          setDisplayText(currentWord.slice(0, displayText.length + 1));
        } else {
          setTimeout(() => setIsDeleting(true), 1500);
        }
      } else {
        if (displayText.length > 0) {
          setDisplayText(currentWord.slice(0, displayText.length - 1));
        } else {
          setIsDeleting(false);
          setCurrentWordIndex((prev) => (prev + 1) % typewriterWords.length);
        }
      }
    }, isDeleting ? 50 : 100);

    return () => clearTimeout(timeout);
  }, [displayText, isDeleting, currentWordIndex]);

  // Fetch leaderboard
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/leaderboard?limit=5`)
      .then(res => res.json())
      .then(data => setLeaderboard(data))
      .catch(console.error);
  }, []);

  const features = [
    { icon: BookOpen, title: 'Curated Content', description: 'Expert-backed articles, news, and tutorials tailored to your needs.' },
    { icon: Brain, title: 'Smart Quizzes', description: 'Test your knowledge and earn SkinPoints while learning.' },
    { icon: Sparkles, title: 'Personalized Advice', description: 'AI-powered daily tips based on your unique profile.' },
    { icon: ShoppingBag, title: 'Real Products', description: 'Discover products from the Open Beauty Facts database.' },
    { icon: Search, title: 'Smart Search', description: 'Find exactly what you need with powerful search & filters.' },
    { icon: Trophy, title: 'Leaderboard', description: 'Compete with others and climb the SkinPoints rankings.' },
  ];

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const handleGoogleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  // Set page title
  useEffect(() => {
    document.title = 'SkinUse | Your Intelligent Skin & Hair Care Companion';
  }, []);

  return (
    <div className="min-h-screen flex flex-col overflow-hidden">
      <SEO {...PAGE_SEO.home} url="/" />
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-lg border-b border-border/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
              <span className="font-serif text-xl font-semibold tracking-tight">SkinUse</span>
              <span className="w-1 h-1 bg-gold rounded-full animate-pulse"></span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" className="text-sm" data-testid="landing-signin">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button className="btn-primary text-sm" data-testid="landing-getstarted">Get Started</Button>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section with Dynamic Elements */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 relative min-h-[90vh] flex items-center">
        <div className="max-w-7xl mx-auto w-full">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="animate-fade-in z-10">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold/10 rounded-sm mb-6 animate-pulse">
                <Sparkles className="w-4 h-4 text-gold" />
                <span className="text-xs font-semibold uppercase tracking-wider text-gold">The Intelligent Vanity</span>
              </div>
              
              {/* Animated Title */}
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold leading-tight mb-6">
                Your{' '}
                <span className="relative inline-block">
                  <span className="gold-text">{displayText}</span>
                  <span className="animate-blink text-gold">|</span>
                </span>
                <br />
                <span className="text-shimmer">Intelligence</span> Platform
              </h1>
              
              <p className="text-lg text-muted-foreground max-w-lg mb-8 leading-relaxed opacity-0 animate-fade-in" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
                Personalized advice, curated content, and smart recommendations powered by dermatological expertise and AI.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 opacity-0 animate-fade-in" style={{ animationDelay: '0.5s', animationFillMode: 'forwards' }}>
                <Link to="/register">
                  <Button className="btn-gold w-full sm:w-auto group" data-testid="hero-getstarted">
                    Get Started Free
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Button 
                  variant="outline" 
                  className="w-full sm:w-auto uppercase tracking-wider text-xs font-bold h-12 px-8 hover:border-gold hover:text-gold transition-colors"
                  onClick={handleGoogleLogin}
                  data-testid="hero-google-login"
                >
                  Continue with Google
                </Button>
              </div>

              {/* Stats */}
              <div className="flex gap-8 mt-12 opacity-0 animate-fade-in" style={{ animationDelay: '0.7s', animationFillMode: 'forwards' }}>
                <div className="text-center">
                  <p className="font-serif text-3xl font-semibold text-gold counter" data-testid="stat-content">{stats.content}</p>
                  <p className="text-xs text-muted-foreground mt-1">Content Pieces</p>
                </div>
                <div className="text-center">
                  <p className="font-serif text-3xl font-semibold text-gold" data-testid="stat-quizzes">{stats.quizzes}</p>
                  <p className="text-xs text-muted-foreground mt-1">Quiz Questions</p>
                </div>
                <div className="text-center">
                  <p className="font-serif text-3xl font-semibold text-gold" data-testid="stat-products">{stats.products}</p>
                  <p className="text-xs text-muted-foreground mt-1">Real Products</p>
                </div>
              </div>
            </div>

            {/* Hero Image Carousel */}
            <div className="relative animate-fade-in h-[500px] lg:h-[600px]" style={{ animationDelay: '0.2s' }}>
              {heroImages.map((image, index) => (
                <div
                  key={index}
                  className={`absolute inset-0 rounded-sm overflow-hidden transition-all duration-1000 ${
                    index === currentImageIndex 
                      ? 'opacity-100 scale-100' 
                      : 'opacity-0 scale-105'
                  }`}
                >
                  <img
                    src={image.url}
                    alt={image.alt}
                    className="w-full h-full object-cover"
                  />
                  {/* Gradient overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-background/60 via-transparent to-transparent" />
                </div>
              ))}

              {/* Image indicators */}
              <div className="absolute bottom-6 left-1/2 -translate-x-1/2 flex gap-2">
                {heroImages.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setCurrentImageIndex(index)}
                    className={`w-2 h-2 rounded-full transition-all ${
                      index === currentImageIndex 
                        ? 'bg-gold w-6' 
                        : 'bg-white/50 hover:bg-white/80'
                    }`}
                    aria-label={`Go to slide ${index + 1}`}
                  />
                ))}
              </div>

              {/* Floating card */}
              <div className="absolute -bottom-6 -left-6 glass p-6 max-w-[280px] animate-float">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-sm bg-gold/20 flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-gold" />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground">Daily Advice</p>
                    <p className="text-sm font-semibold">Personalized for you</p>
                  </div>
                </div>
                <p className="text-sm text-muted-foreground">AI-powered tips based on your unique profile</p>
              </div>
            </div>
          </div>
        </div>

        {/* Background decoration */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gold/5 rounded-full blur-3xl pointer-events-none" />
      </section>

      {/* Features Grid */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-secondary/30 relative">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold mb-4">
              <span className="text-shimmer">Everything</span> You Need
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              A comprehensive platform designed for your skin and hair care journey
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <Card 
                key={index} 
                className="bg-card border-none shadow-card card-hover group opacity-0 animate-slide-up" 
                style={{ animationDelay: `${index * 0.1}s`, animationFillMode: 'forwards' }}
                data-testid={`feature-card-${index}`}
              >
                <CardContent className="p-8">
                  <div className="w-12 h-12 rounded-sm bg-gold/10 flex items-center justify-center mb-6 group-hover:bg-gold/20 group-hover:scale-110 transition-all">
                    <feature.icon className="w-6 h-6 text-gold" />
                  </div>
                  <h3 className="font-serif text-lg font-semibold mb-2 group-hover:text-gold transition-colors">{feature.title}</h3>
                  <p className="text-sm text-muted-foreground">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Content Sections Preview */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-12">
            <div>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold mb-2">Explore Content</h2>
              <p className="text-muted-foreground">News, articles, videos, and more</p>
            </div>
            <Link to="/daily-drop" className="hidden sm:flex items-center gap-2 text-sm font-semibold hover:text-gold transition-colors" data-testid="view-all-content">
              View All <ChevronRight className="w-4 h-4" />
            </Link>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {/* Daily Drop */}
            <Link to="/daily-drop" className="group" data-testid="section-daily-drop">
              <Card className="bg-card border-none shadow-card card-hover overflow-hidden h-full">
                <div className="aspect-video overflow-hidden">
                  <img
                    src="https://images.pexels.com/photos/16233812/pexels-photo-16233812.jpeg?auto=compress&cs=tinysrgb&w=600"
                    alt="Daily Drop"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-gold/10 rounded-sm mb-4">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gold">News</span>
                  </div>
                  <h3 className="font-serif text-xl font-semibold mb-2 group-hover:text-gold transition-colors">The Daily Drop</h3>
                  <p className="text-sm text-muted-foreground">Stay updated with the latest skincare and haircare news</p>
                </CardContent>
              </Card>
            </Link>

            {/* Deep Dives */}
            <Link to="/deep-dives" className="group" data-testid="section-deep-dives">
              <Card className="bg-card border-none shadow-card card-hover overflow-hidden h-full">
                <div className="aspect-video overflow-hidden">
                  <img
                    src="https://images.pexels.com/photos/6925487/pexels-photo-6925487.jpeg?auto=compress&cs=tinysrgb&w=600"
                    alt="Deep Dives"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                </div>
                <CardContent className="p-6">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-gold/10 rounded-sm mb-4">
                    <BookOpen className="w-3 h-3 text-gold" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-gold">Articles</span>
                  </div>
                  <h3 className="font-serif text-xl font-semibold mb-2 group-hover:text-gold transition-colors">Deep Dives</h3>
                  <p className="text-sm text-muted-foreground">In-depth guides and expert articles</p>
                </CardContent>
              </Card>
            </Link>

            {/* Video Vault */}
            <Link to="/video-vault" className="group" data-testid="section-video-vault">
              <Card className="bg-card border-none shadow-card card-hover overflow-hidden h-full">
                <div className="aspect-video overflow-hidden relative">
                  <img
                    src="https://images.pexels.com/photos/30797176/pexels-photo-30797176.jpeg?auto=compress&cs=tinysrgb&w=600"
                    alt="Video Vault"
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="w-6 h-6 text-primary fill-current ml-1" />
                    </div>
                  </div>
                </div>
                <CardContent className="p-6">
                  <div className="inline-flex items-center gap-2 px-3 py-1 bg-gold/10 rounded-sm mb-4">
                    <Play className="w-3 h-3 text-gold" />
                    <span className="text-xs font-semibold uppercase tracking-wider text-gold">Videos</span>
                  </div>
                  <h3 className="font-serif text-xl font-semibold mb-2 group-hover:text-gold transition-colors">Video Vault</h3>
                  <p className="text-sm text-muted-foreground">Watch tutorials and expert demonstrations</p>
                </CardContent>
              </Card>
            </Link>
          </div>
        </div>
      </section>

      {/* Leaderboard Preview */}
      {leaderboard.length > 0 && (
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-secondary/30">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold/20 rounded-sm mb-6">
                  <Trophy className="w-4 h-4 text-gold" />
                  <span className="text-xs font-semibold uppercase tracking-wider text-gold">Leaderboard</span>
                </div>
                <h2 className="font-serif text-3xl sm:text-4xl font-semibold mb-6">
                  Compete & <span className="gold-text">Climb</span>
                </h2>
                <p className="text-muted-foreground max-w-lg mb-8 leading-relaxed">
                  Earn SkinPoints by reading content, taking quizzes, and engaging with the platform. See how you rank against other beauty enthusiasts!
                </p>
                <Link to="/register">
                  <Button className="btn-gold" data-testid="join-leaderboard">
                    Join the Competition
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>

              {/* Leaderboard Card */}
              <Card className="bg-card border-none shadow-float" data-testid="leaderboard-preview">
                <CardContent className="p-8">
                  <h3 className="font-serif text-xl font-semibold mb-6 flex items-center gap-2">
                    <Trophy className="w-5 h-5 text-gold" />
                    Top Players
                  </h3>
                  <div className="space-y-4">
                    {leaderboard.slice(0, 5).map((user, index) => (
                      <div 
                        key={user.user_id} 
                        className={`flex items-center gap-4 p-3 rounded-sm transition-colors ${
                          index === 0 ? 'bg-gold/10' : 'hover:bg-secondary'
                        }`}
                      >
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                          index === 0 ? 'bg-gold text-black' : 
                          index === 1 ? 'bg-gray-300 text-black' : 
                          index === 2 ? 'bg-amber-600 text-white' : 
                          'bg-secondary text-muted-foreground'
                        }`}>
                          {index + 1}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate">{user.name}</p>
                        </div>
                        <div className="flex items-center gap-1">
                          <Sparkles className="w-4 h-4 text-gold" />
                          <span className="font-semibold text-gold">{user.skin_points}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>
      )}

      {/* SkinPoints Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-primary text-primary-foreground">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-gold/20 rounded-sm mb-6">
                <Sparkles className="w-4 h-4 text-gold" />
                <span className="text-xs font-semibold uppercase tracking-wider text-gold">Rewards</span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold mb-6">
                Earn SkinPoints, <br />Unlock Rewards
              </h2>
              <p className="text-primary-foreground/70 max-w-lg mb-8 leading-relaxed">
                Every article you read, quiz you take, and interaction earns you SkinPoints. Redeem them for exclusive content, discounts, and premium features.
              </p>
              <div className="flex gap-12">
                <div className="text-center">
                  <p className="font-serif text-4xl font-semibold text-gold animate-pulse">10+</p>
                  <p className="text-sm text-primary-foreground/60">Points per article</p>
                </div>
                <div className="text-center">
                  <p className="font-serif text-4xl font-semibold text-gold animate-pulse" style={{ animationDelay: '0.2s' }}>20+</p>
                  <p className="text-sm text-primary-foreground/60">Points per quiz</p>
                </div>
                <div className="text-center">
                  <p className="font-serif text-4xl font-semibold text-gold animate-pulse" style={{ animationDelay: '0.4s' }}>100</p>
                  <p className="text-sm text-primary-foreground/60">Welcome bonus</p>
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="aspect-square rounded-sm overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/32291052/pexels-photo-32291052.jpeg?auto=compress&cs=tinysrgb&w=600"
                  alt="Skincare products"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="max-w-3xl mx-auto text-center relative z-10">
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold mb-6">
            Ready to Transform Your Routine?
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Join thousands who are already using SkinUse for personalized skin and hair care guidance.
          </p>
          <Link to="/register">
            <Button className="btn-gold group" data-testid="cta-getstarted">
              Get Started Free
              <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
            </Button>
          </Link>
        </div>
        
        {/* Background decoration */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-gold/5 rounded-full blur-3xl pointer-events-none" />
      </section>

      <Footer />
    </div>
  );
}
